<script setup lang="ts">
import type { CardImageOverlayProps } from '@/components/CardImageOverlay/CardImageOverlay.vue';

import { CardImageOverlay } from '@/components/CardImageOverlay';
import WheelGesturesPlugin from 'embla-carousel-wheel-gestures';

import {
	Carousel,
	CarouselContent,
	CarouselGenericAction,
	CarouselItem,
	CarouselItemSelector
} from '~/components/ui/carousel';

interface TabCardCarouselProps {
	cards: CardImageOverlayProps[];
}

const props = defineProps<TabCardCarouselProps>();

const emit = defineEmits<{
	cardClick: [cardIndex: number, event: MouseEvent];
}>();

const plugins = [WheelGesturesPlugin()];

const carouselContainer = ref();
const carouselItemSelectorContainer = ref();

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef: carouselContainer,
	carouselItemSelectorContainerRef: carouselItemSelectorContainer,
	cyclic: true
});

function handlePreviousSlideArrowClick(): void {
	scrollTo(currentCarouselItemIndex.value - 1);
}

function handleNextSlideArrowClick(): void {
	scrollTo(currentCarouselItemIndex.value + 1);
}

function handleCardClick(cardIndex: number, event: MouseEvent): void {
	emit('cardClick', cardIndex, event);
}

watch(
	() => props.cards,
	() => {
		nextTick(() => {
			scrollTo(0);
		});
	}
);

// Expose carousel controls for parent component if needed
defineExpose({
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
});
</script>

<template>
	<Carousel
		ref="carouselContainer"
		class="flex w-full flex-col gap-5 md:gap-6"
		:plugins="plugins"
		:opts="{
			align: 'center',
			slidesToScroll: 1,
			breakpoints: {
				'(min-width: 768px)': { align: 'start' },
				'(min-width: 1024px)': { align: 'start' }
			}
		}"
	>
		<CarouselContent class="flex gap-4">
			<CarouselItem
				v-for="(card, cardIndex) in cards"
				:key="`${cardIndex}|${card.to ?? ''}|${card.image.src}`"
				class="h-[280px] basis-[89%] overflow-hidden rounded-lg md:h-[320px] md:basis-[47.2%] lg:h-[320px] lg:basis-[calc(33.33333%-0.75rem)] xl:h-[400px]"
			>
				<CardImageOverlay
					:image="card.image"
					:content="card.content"
					:to="card.to"
					:show-arrow-icon="card.showArrowIcon"
					:style-config="card.styleConfig"
					@click="handleCardClick(cardIndex, $event)"
				/>
			</CarouselItem>
		</CarouselContent>

		<div class="flex w-full items-center gap-5 lg:invisible lg:absolute">
			<div
				ref="carouselItemSelectorContainer"
				class="flex w-full flex-wrap justify-end gap-2 md:gap-5"
			>
				<CarouselItemSelector
					v-for="(_, index) in scrollableSlidesNumber"
					:key="`selector-${index}`"
					:active="currentCarouselItemIndex === index"
					:delay="2000"
					:dynamic-width="carouselDynamicItemWidth"
					@click="scrollTo(index)"
				/>
			</div>

			<div class="flex gap-0">
				<CarouselGenericAction @click="handlePreviousSlideArrowClick">
					<IconMaterialSymbolsChevronLeft class="size-7 md:size-8" />
				</CarouselGenericAction>
				<CarouselGenericAction @click="handleNextSlideArrowClick">
					<IconMaterialSymbolsChevronRight class="size-7 md:size-8" />
				</CarouselGenericAction>
			</div>
		</div>
	</Carousel>
</template>
